import { useEffect, type RefObject } from 'react';

/** How far beyond the title's own half width the flies range, and how tall their ellipse is (half height), in px. */
const EXTRA_WIDTH = 34;
const HALF_HEIGHT = 26;
/** The ellipse's centre sits a little above the middle of the title. */
const CENTRE_Y = -3;
const MAX_STEP = 0.05;

type Mode = 'rest' | 'dart' | 'hover';

interface Fly {
  x: number;
  y: number;
  heading: number;
  mode: Mode;
  /** Seconds left in `rest` or `hover`. */
  wait: number;
  tx: number;
  ty: number;
  speed: number;
  phase: number;
}

const between = (low: number, high: number): number => low + Math.random() * (high - low);

/** The reduced-motion spots: right of the title, left of it, and above it. */
export function staticSpots(halfWidth: number): readonly (readonly [number, number, number])[] {
  return [
    [halfWidth + EXTRA_WIDTH * 0.6, CENTRE_Y, Math.PI / 2],
    [-(halfWidth + EXTRA_WIDTH * 0.6), CENTRE_Y + 4, -Math.PI / 2],
    [halfWidth * 0.3, CENTRE_Y - HALF_HEIGHT * 0.9, 0.3],
  ];
}

function place(element: HTMLElement, x: number, y: number, heading: number): void {
  element.style.transform = `translate(${x.toFixed(2)}px, ${y.toFixed(2)}px) rotate(${heading.toFixed(3)}rad)`;
}

/**
 * Loose, lifelike flight for a few small flies around a title: each rests, darts to a new place on an ellipse around the
 * title (slowing as it nears it, nose along the way it goes), hovers with a tiny jitter, and goes again. The flies are
 * moved by writing their transforms straight to the DOM, not through React state. Under reduced motion they sit at fixed
 * spots and nothing runs; the loop stops while the tab is hidden and on unmount.
 */
export function useFlies(host: RefObject<HTMLElement | null>, flies: RefObject<(HTMLElement | null)[]>, count: number): void {
  useEffect(() => {
    const box = host.current;
    if (box === null) return undefined;
    const elements = (): HTMLElement[] => flies.current.filter((element): element is HTMLElement => element !== null).slice(0, count);
    const halfWidth = (): number => box.offsetWidth / 2;

    const reduced = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      const spots = staticSpots(halfWidth());
      elements().forEach((element, index) => {
        const spot = spots[index % spots.length] ?? [0, 0, 0];
        place(element, spot[0], spot[1], spot[2]);
      });
      return undefined;
    }

    const targetOf = (): { x: number; y: number } => {
      const angle = Math.random() * Math.PI * 2;
      const factor = between(0.75, 1.15);
      return { x: Math.cos(angle) * (halfWidth() + EXTRA_WIDTH) * factor, y: CENTRE_Y + Math.sin(angle) * HALF_HEIGHT * factor };
    };
    const state: Fly[] = elements().map(() => {
      const start = targetOf();
      return { x: start.x, y: start.y, heading: Math.random() * Math.PI * 2, mode: 'rest', wait: between(0.2, 1.8), tx: start.x, ty: start.y, speed: 0, phase: Math.random() * 10 };
    });

    let frame = 0;
    let last = 0;
    let clock = 0;
    const tick = (now: number): void => {
      const dt = Math.min(MAX_STEP, last === 0 ? 0 : (now - last) / 1000);
      last = now;
      clock += dt;
      const items = elements();
      state.forEach((fly, index) => {
        const element = items[index];
        if (element === undefined) return;
        if (fly.mode === 'dart') {
          const dx = fly.tx - fly.x;
          const dy = fly.ty - fly.y;
          const distance = Math.hypot(dx, dy);
          if (distance < 3) {
            fly.mode = Math.random() < 0.55 ? 'hover' : 'rest';
            fly.wait = fly.mode === 'hover' ? between(0.8, 2.2) : between(0.6, 3.2);
          } else {
            const slow = Math.min(1, distance / 40 + 0.25);
            const step = Math.min(distance, fly.speed * slow * dt);
            fly.x += (dx / distance) * step + Math.sin(clock * 9 + fly.phase) * 0.35;
            fly.y += (dy / distance) * step + Math.cos(clock * 11 + fly.phase) * 0.35;
            // the nose along the way it goes, eased toward it
            const want = Math.atan2(dy, dx) + Math.PI / 2;
            let turn = want - fly.heading;
            while (turn > Math.PI) turn -= Math.PI * 2;
            while (turn < -Math.PI) turn += Math.PI * 2;
            fly.heading += turn * (1 - Math.exp(-14 * dt));
          }
        } else {
          fly.wait -= dt;
          if (fly.mode === 'hover') {
            fly.x += Math.sin(clock * 23 + fly.phase) * 0.18;
            fly.y += Math.cos(clock * 29 + fly.phase) * 0.18;
          }
          if (fly.wait <= 0) {
            const next = targetOf();
            fly.tx = next.x;
            fly.ty = next.y;
            fly.speed = between(60, 110);
            fly.mode = 'dart';
          }
        }
        place(element, fly.x, fly.y, fly.heading);
      });
      frame = window.requestAnimationFrame(tick);
    };

    const start = (): void => {
      if (frame === 0) {
        last = 0;
        frame = window.requestAnimationFrame(tick);
      }
    };
    const stop = (): void => {
      if (frame !== 0) window.cancelAnimationFrame(frame);
      frame = 0;
    };
    const onVisibility = (): void => {
      if (document.hidden) stop();
      else start();
    };
    document.addEventListener('visibilitychange', onVisibility);
    if (!document.hidden) start();
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      stop();
    };
  }, [host, flies, count]);
}
